import * as i0 from '@angular/core';
import { InjectionToken, signal, computed, Injectable, inject, NgZone, NgModule, makeEnvironmentProviders } from '@angular/core';
import { HttpBackend, HttpRequest, HttpResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, BehaviorSubject, timer, isObservable, throwError } from 'rxjs';
import { filter, map, retry, shareReplay, catchError, take, concatMap } from 'rxjs/operators';
import * as vlq from 'vlq';
import { DatePipe } from '@angular/common';

/**
 * Injection token of logger config
 */
const TOKEN_LOGGER_CONFIG = /*#__PURE__*/new InjectionToken("TOKEN_LOGGER_CONFIG");
const NgxLogLevels = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  LOG: 3,
  WARN: 4,
  ERROR: 5,
  FATAL: 6,
  OFF: 7
};
const NgxLogLevelsReversed = Object.entries(NgxLogLevels).reduce((acc, [key, value]) => ({
  ...acc,
  [value]: key
}), {});
function getLevelName(level) {
  return NgxLogLevelsReversed[level];
}

/**
 * Configuration engine using Angular signals for reactive state management
 * Provides immutable config updates and computed derived values
 */
class NGXLoggerConfigEngine {
  #config;
  // Internal computed signals for reactive state
  levelSignal;
  serverLogLevelSignal;
  config;
  constructor(initialConfig) {
    this.#config = signal(initialConfig, ...(ngDevMode ? [{
      debugName: "#config"
    }] : []));
    this.config = this.#config.asReadonly();
    this.levelSignal = computed(() => this.#config().level, ...(ngDevMode ? [{
      debugName: "levelSignal"
    }] : []));
    this.serverLogLevelSignal = computed(() => this.#config().serverLogLevel ?? NgxLogLevels.OFF, ...(ngDevMode ? [{
      debugName: "serverLogLevelSignal"
    }] : []));
  }
  /**
   * Get a readonly access to the level configured for the NGXLogger
   * @returns The configured minimum log level
   */
  get level() {
    return this.levelSignal();
  }
  /**
   * Get a readonly access to the serverLogLevel configured for the NGXLogger
   * @returns The configured minimum server log level
   */
  get serverLogLevel() {
    return this.serverLogLevelSignal();
  }
  /**
   * Updates the entire configuration
   * @param config New configuration to set
   */
  updateConfig(config) {
    this.#config.set({
      ...config
    });
  }
  /**
   * Updates the config partially
   * This is useful if you want to update only one parameter of the config
   * @param partialConfig Partial configuration to merge with current config
   */
  partialUpdateConfig(partialConfig) {
    this.#config.update(current => ({
      ...current,
      ...partialConfig
    }));
  }
  /**
   * Get a snapshot of the current configuration
   * @returns A cloned copy of the current configuration
   */
  getConfig() {
    return {
      ...this.#config()
    };
  }
}

/**
 * Injection token of logger config engine factory
 */
const TOKEN_LOGGER_CONFIG_ENGINE_FACTORY = /*#__PURE__*/new InjectionToken("TOKEN_LOGGER_CONFIG_ENGINE_FACTORY");

/**
 * Factory for creating NGXLoggerConfigEngine instances
 * Implements the Factory pattern for config engine creation
 */
let NGXLoggerConfigEngineFactory = /*#__PURE__*/(() => {
  class NGXLoggerConfigEngineFactory {
    /**
     * Creates a new config engine instance
     * @param config Initial configuration
     * @returns A new config engine instance
     */
    provideConfigEngine(config) {
      return new NGXLoggerConfigEngine(config);
    }
    static ɵfac = function NGXLoggerConfigEngineFactory_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NGXLoggerConfigEngineFactory)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NGXLoggerConfigEngineFactory,
      factory: NGXLoggerConfigEngineFactory.ɵfac
    });
  }
  return NGXLoggerConfigEngineFactory;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Injection token of logger mapper service
 */
const TOKEN_LOGGER_MAPPER_SERVICE = /*#__PURE__*/new InjectionToken("TOKEN_LOGGER_MAPPER_SERVICE");
let NGXLoggerMapperService = /*#__PURE__*/(() => {
  class NGXLoggerMapperService {
    httpBackend = inject(HttpBackend, {
      optional: true
    });
    /** cache for source maps, key is source map location, ie. 'http://localhost:4200/main.js.map' */
    sourceMapCache = new Map();
    /** cache for specific log position, key is the dist position, ie 'main.js:339:21' */
    logPositionCache = new Map();
    /**
     * Returns the log position of the caller
     * If sourceMaps are enabled, it attemps to get the source map from the server, and use that to parse the position
     * @param config
     * @param metadata
     * @returns
     */
    getLogPosition(config,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    metadata) {
      const stackLine = this.getStackLine(config);
      // if we were not able to parse the stackLine, just return an empty Log Position
      if (!stackLine) {
        return of({
          fileName: "",
          lineNumber: 0,
          columnNumber: 0
        });
      }
      const logPosition = this.getLocalPosition(stackLine);
      if (!config.enableSourceMaps) {
        return of(logPosition);
      }
      const sourceMapLocation = this.getSourceMapLocation(stackLine);
      return this.getSourceMap(sourceMapLocation, logPosition);
    }
    /**
     * Get the stackline of the original caller
     * @param config
     * @returns null if stackline was not found
     */
    getStackLine(config) {
      // Here are different examples of stacktrace
      // Firefox (last line is the user code, the 4 first are ours):
      // getStackLine@http://localhost:4200/main.js:358:23
      // getCallerDetails@http://localhost:4200/main.js:557:44
      // _log@http://localhost:4200/main.js:830:28
      // debug@http://localhost:4200/main.js:652:14
      // handleLog@http://localhost:4200/main.js:1158:29
      // Chrome and Edge (last line is the user code):
      // Error
      // at Function.getStackLine (ngx-logging-kit.js:329)
      // at NGXMapperService.getCallerDetails (ngx-logging-kit.js:528)
      // at NGXLogger._log (ngx-logging-kit.js:801)
      // at NGXLogger.info (ngx-logging-kit.js:631)
      // at AppComponent.handleLog (app.component.ts:38)
      const error = new Error("dummy error to get stacktrace, ignore it, it's just to get the stacktrace");
      if (!error.stack) {
        return null;
      }
      let defaultProxy = 4; // We make 4 functions call before getting here
      const firstStackLine = error.stack.split("\n")[0];
      if (!firstStackLine.includes(".js:")) {
        // The stacktrace starts with no function call (example in Chrome or Edge)
        defaultProxy = defaultProxy + 1;
      }
      return error.stack.split("\n")[defaultProxy + (config.proxiedSteps ?? 0)];
    }
    /**
     * Get position of caller without using sourceMaps
     * @param stackLine
     * @returns
     */
    getLocalPosition(stackLine) {
      // strip base path, then parse filename, line, and column, stackline looks like this :
      // Firefox
      // handleLog@http://localhost:4200/main.js:1158:29
      // Chrome and Edge
      // at AppComponent.handleLog (app.component.ts:38)
      const positionStartIndex = stackLine.lastIndexOf("/");
      let positionEndIndex = stackLine.indexOf(")");
      if (positionEndIndex < 0) {
        positionEndIndex = stackLine.length;
      }
      const position = stackLine.substring(positionStartIndex + 1, positionEndIndex);
      const dataArray = position.split(":");
      if (dataArray.length === 3) {
        return {
          fileName: dataArray[0],
          lineNumber: +dataArray[1],
          columnNumber: +dataArray[2]
        };
      }
      return {
        fileName: "unknown",
        lineNumber: 0,
        columnNumber: 0
      };
    }
    /**
     * Gets the URL of the sourcemap (the URL can be relative or absolute, it is browser dependant)
     * @param stackLine
     * @returns
     */
    getSourceMapLocation(stackLine) {
      const file = this.getTranspileLocation(stackLine);
      const mapFullPath = file.substring(0, file.lastIndexOf(":"));
      return mapFullPath.substring(0, mapFullPath.lastIndexOf(":")) + ".map";
    }
    /**
     * does the http get request to get the source map
     * @param sourceMapLocation
     * @param distPosition
     */
    getSourceMap(sourceMapLocation, distPosition) {
      const req = new HttpRequest("GET", sourceMapLocation);
      const distPositionKey = `${distPosition.fileName}:${distPosition.lineNumber.toString()}:${distPosition.columnNumber.toString()}`;
      // if the specific log position is already in cache return it
      if (this.logPositionCache.has(distPositionKey)) {
        return this.logPositionCache.get(distPositionKey);
      }
      // otherwise check if the source map is already cached for given source map location
      if (!this.sourceMapCache.has(sourceMapLocation)) {
        if (this.httpBackend === null) {
          console.error("NGXLogger : Can't get sourcemap because HttpBackend is not provided. You need to import HttpClientModule");
          this.sourceMapCache.set(sourceMapLocation, of(undefined));
        } else {
          // obtain the source map if not cached
          this.sourceMapCache.set(sourceMapLocation, this.httpBackend.handle(req).pipe(filter(e => e instanceof HttpResponse), map(httpResponse => httpResponse.body), retry(3), shareReplay(1)));
        }
      }
      // at this point the source map is cached, use it to get specific log position mapping
      const logPosition$ = this.sourceMapCache.get(sourceMapLocation).pipe(map(sourceMap => {
        // sourceMap can be null if HttpBackend is not provided for example
        if (!sourceMap) {
          return distPosition;
        }
        // map generated position to source position
        return this.getMapping(sourceMap, distPosition);
      }), catchError(() => of(distPosition)), shareReplay(1));
      // store specific log position in cache for given dest position and return it
      this.logPositionCache.set(distPositionKey, logPosition$);
      return logPosition$;
    }
    getTranspileLocation(stackLine) {
      // Example stackLine:
      // Firefox : getStackLine@http://localhost:4200/main.js:358:23
      // Chrome and Edge : at Function.getStackLine (ngx-logging-kit.js:329)
      let locationStartIndex = stackLine.indexOf("(");
      if (locationStartIndex < 0) {
        locationStartIndex = stackLine.lastIndexOf("@");
        if (locationStartIndex < 0) {
          locationStartIndex = stackLine.lastIndexOf(" ");
        }
      }
      let locationEndIndex = stackLine.indexOf(")");
      if (locationEndIndex < 0) {
        locationEndIndex = -1;
      }
      return stackLine.substring(locationStartIndex + 1, locationEndIndex);
    }
    getMapping(sourceMap, position) {
      // => ';' indicates end of a line
      // => ',' separates mappings in a line
      // decoded mapping => [ generatedCodeColumn, sourceFileIndex, sourceCodeLine, sourceCodeColumn, nameIndex ]
      let sourceFileIndex = 0,
        // second field
        sourceCodeLine = 0,
        // third field
        sourceCodeColumn = 0; // fourth field
      const lines = sourceMap.mappings.split(";");
      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        // reset column position to 0 after each line
        let generatedCodeColumn = 0;
        // decode sections in line
        const columns = lines[lineIndex].split(",");
        for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
          const decodedSection = vlq.decode(columns[columnIndex]);
          if (decodedSection.length >= 4) {
            // update relative positions
            generatedCodeColumn += decodedSection[0];
            sourceFileIndex += decodedSection[1];
            sourceCodeLine += decodedSection[2];
            sourceCodeColumn += decodedSection[3];
          }
          // check if matching map
          if (lineIndex === position.lineNumber) {
            if (generatedCodeColumn === position.columnNumber) {
              // matching column and line found
              return {
                fileName: sourceMap.sources[sourceFileIndex],
                lineNumber: sourceCodeLine,
                columnNumber: sourceCodeColumn
              };
            } else if (columnIndex + 1 === columns.length) {
              // matching column not found, but line is correct
              return {
                fileName: sourceMap.sources[sourceFileIndex],
                lineNumber: sourceCodeLine,
                columnNumber: 0
              };
            }
          }
        }
      }
      // failed if reached
      return {
        fileName: "unknown",
        lineNumber: 0,
        columnNumber: 0
      };
    }
    static ɵfac = function NGXLoggerMapperService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NGXLoggerMapperService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NGXLoggerMapperService,
      factory: NGXLoggerMapperService.ɵfac
    });
  }
  return NGXLoggerMapperService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Injection token of logger metadata service
 */
const TOKEN_LOGGER_METADATA_SERVICE = /*#__PURE__*/new InjectionToken("TOKEN_LOGGER_METADATA_SERVICE");
let NGXLoggerMetadataService = /*#__PURE__*/(() => {
  class NGXLoggerMetadataService {
    datePipe = inject(DatePipe, {
      optional: true
    });
    getMetadata(level, config,
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    message, additional) {
      const metadata = {
        level: level,
        additional: additional
      };
      // The user can send a function
      // This is useful in order to compute string concatenation only when the log will actually be written
      if (message && typeof message === "function") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        metadata.message = message();
      } else {
        metadata.message = message;
      }
      metadata.timestamp = this.computeTimestamp(config);
      return metadata;
    }
    computeTimestamp(config) {
      const defaultTimestamp = new Date().toISOString();
      if (config.timestampFormat) {
        if (this.datePipe === null) {
          console.error("NGXLogger : Can't use timeStampFormat because DatePipe is not provided. You need to provide DatePipe");
          return defaultTimestamp;
        } else {
          return this.datePipe.transform(new Date(), config.timestampFormat) ?? defaultTimestamp;
        }
      }
      return defaultTimestamp;
    }
    static ɵfac = function NGXLoggerMetadataService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NGXLoggerMetadataService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NGXLoggerMetadataService,
      factory: NGXLoggerMetadataService.ɵfac
    });
  }
  return NGXLoggerMetadataService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// I kept this class alive only to avoid a breaking change with the old version
// This class does not implement anything so it is useless and the interface is enough
/**
 * @deprecated this class does not implement anything thus being useless, you should rather implements @see INGXLoggerMonitor
 */
class NGXLoggerMonitor {}

/**
 * Injection token of logger metadata service
 */
const TOKEN_LOGGER_RULES_SERVICE = /*#__PURE__*/new InjectionToken("TOKEN_LOGGER_RULES_SERVICE");
let NGXLoggerRulesService = /*#__PURE__*/(() => {
  class NGXLoggerRulesService {
    shouldCallWriter(level, config,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    message,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    additional) {
      return !config.disableConsoleLogging && level >= config.level;
    }
    shouldCallServer(level, config,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    message,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    additional) {
      return !!config.serverLoggingUrl && level >= (config.serverLogLevel ?? NgxLogLevels.OFF);
    }
    shouldCallMonitor(level, config, message, additional) {
      // The default behavior is to call the monitor only if the writer or the server is called
      return this.shouldCallWriter(level, config, message, additional) || this.shouldCallServer(level, config, message, additional);
    }
    static ɵfac = function NGXLoggerRulesService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NGXLoggerRulesService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NGXLoggerRulesService,
      factory: NGXLoggerRulesService.ɵfac
    });
  }
  return NGXLoggerRulesService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Injection token of logger server service
 */
const TOKEN_LOGGER_SERVER_SERVICE = /*#__PURE__*/new InjectionToken('TOKEN_LOGGER_SERVER_SERVICE');
let NGXLoggerServerService = /*#__PURE__*/(() => {
  class NGXLoggerServerService {
    httpBackend = inject(HttpBackend, {
      optional: true
    });
    ngZone = inject(NgZone, {
      optional: true
    });
    serverCallsQueue = [];
    flushingQueue = new BehaviorSubject(false);
    addToQueueTimer = null;
    ngOnDestroy() {
      this.flushingQueue.complete();
      if (this.addToQueueTimer) {
        this.addToQueueTimer.unsubscribe();
        this.addToQueueTimer = null;
      }
    }
    /**
     * Flush the queue of the logger
     * @param config
     */
    flushQueue(config) {
      this.flushingQueue.next(true);
      // If a timer was set, we cancel it because the queue is flushed
      if (this.addToQueueTimer) {
        this.addToQueueTimer.unsubscribe();
        this.addToQueueTimer = null;
      }
      if (this.serverCallsQueue.length > 0) {
        this.sendToServerAction(this.serverCallsQueue, config);
      }
      this.serverCallsQueue = [];
      this.flushingQueue.next(false);
    }
    /**
     * Sends the content to be logged to the server according to the config
     * @param metadata
     * @param config
     */
    sendToServer(metadata, config) {
      // If there is no batch mode in the config, we send the log call straight to the server as usual
      if ((!config.serverCallsBatchSize || config.serverCallsBatchSize <= 0) && (!config.serverCallsTimer || config.serverCallsTimer <= 0)) {
        this.sendToServerAction(metadata, config);
        return;
      }
      const addLogToQueueAction = () => {
        this.serverCallsQueue.push({
          ...metadata
        });
        // Flush queue when size is reached
        if (!!config.serverCallsBatchSize && this.serverCallsQueue.length > config.serverCallsBatchSize) {
          this.flushQueue(config);
        }
        // Call timer only if it is in the config and timer is not already running
        if ((config.serverCallsTimer ?? 0) > 0 && !this.addToQueueTimer) {
          this.addToQueueTimer = timer(config.serverCallsTimer).subscribe(() => this.flushQueue(config));
        }
      };
      // If queue is being flushed, we need to wait for it to finish before adding other calls
      if (this.flushingQueue.value) {
        this.flushingQueue.pipe(filter(fq => !fq), take(1)).subscribe(() => addLogToQueueAction());
      } else {
        addLogToQueueAction();
      }
    }
    /**
     * Transforms an error object into a readable string (taking only the stack)
     * This is needed because JSON.stringify would return "{}"
     * @param err the error object
     * @returns The stack of the error
     */
    secureErrorObject(err) {
      return err.stack ?? "";
    }
    /**
     * Transforms the additional parameters to avoid any json error when sending the data to the server
     * Basically it just replaces unstringifiable object to a string mentioning an error
     * @param additional The additional data to be sent
     * @returns The additional data secured
     */
    secureAdditionalParameters(additional) {
      return additional.map((next, idx) => {
        try {
          if (next instanceof Error) {
            return this.secureErrorObject(next);
          }
          // We just want to make sure the JSON can be parsed, we do not want to actually change the type
          if (typeof next === "object") {
            JSON.stringify(next);
          }
          return next;
        } catch (e) {
          console.error("NGXLogger: Failed to secure additional parameter", e);
          return `The additional[${idx.toString()}] value could not be parsed using JSON.stringify().`;
        }
      });
    }
    /**
     * Transforms the message so that it can be sent to the server
     * @param message the message to be sent
     * @returns the message secured
     */
    secureMessage(message) {
      try {
        if (message instanceof Error) {
          return this.secureErrorObject(message);
        }
        if (typeof message !== "string") {
          return JSON.stringify(message, null, 2);
        }
        return message;
      } catch (e) {
        console.error("NGXLogger: Failed to secure message", e);
        return 'The provided "message" value could not be parsed with JSON.stringify().';
      }
    }
    /**
     * Edits HttpRequest object before sending request to server
     * @param httpRequest default request object
     * @returns altered httprequest
     */
    alterHttpRequest(httpRequest) {
      return httpRequest;
    }
    /**
     * Sends request to server
     * @param url
     * @param logContent
     * @param options
     * @returns
     */
    logOnServer(url, logContent, options) {
      if (!this.httpBackend) {
        console.error("NGXLogger : Can't log on server because HttpBackend is not provided. You need to import HttpClientModule");
        return of(null);
      }
      // HttpBackend skips all HttpInterceptors
      // They may log errors using this service causing circular calls
      const defaultRequest = new HttpRequest("POST", url, logContent, options);
      let finalRequest;
      const alteredRequest = this.alterHttpRequest(defaultRequest);
      if (isObservable(alteredRequest)) {
        finalRequest = alteredRequest;
      } else {
        finalRequest = of(alteredRequest);
      }
      return finalRequest.pipe(concatMap(req => {
        if (!this.httpBackend) {
          console.error("NGXLogger : HttpBackend is not provided. Cannot send log to server.");
          return throwError(() => new Error("HttpBackend not provided"));
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!req) {
          console.warn("NGXLogger : alterHttpRequest returned an invalid request (observable). Using default one instead");
          return this.httpBackend.handle(defaultRequest);
        }
        return this.httpBackend.handle(req);
      }), filter(e => e instanceof HttpResponse),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      map(httpResponse => httpResponse.body));
    }
    /**
     * Customise the data sent to the API
     * @param metadata the data provided by NGXLogger
     * @returns the data that will be sent to the API in the body
     */
    customiseRequestBody(metadata) {
      // In our API the body is not customised
      return metadata;
    }
    sendToServerAction(metadata, config) {
      let requestBody;
      const secureMetadata = pMetadata => {
        // Copying metadata locally because we don't want to change the object for the caller
        const securedMetadata = {
          ...pMetadata
        };
        securedMetadata.additional = this.secureAdditionalParameters(securedMetadata.additional ?? []);
        securedMetadata.message = this.secureMessage(securedMetadata.message);
        return securedMetadata;
      };
      if (Array.isArray(metadata)) {
        requestBody = metadata.map(secureMetadata);
      } else {
        requestBody = secureMetadata(metadata);
      }
      // Allow users to customise the data sent to the API
      requestBody = this.customiseRequestBody(requestBody);
      const headers = config.customHttpHeaders ?? new HttpHeaders();
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      const logOnServerAction = () => {
        this.logOnServer(config.serverLoggingUrl, requestBody, {
          headers,
          params: config.customHttpParams ?? new HttpParams(),
          responseType: config.httpResponseType ?? "json",
          withCredentials: config.withCredentials ?? false
        }).pipe(catchError(err => {
          // Do not use NGXLogger here because this could cause an infinite loop
          console.error("NGXLogger: Failed to log on server", err);
          return throwError(() => err);
        })).subscribe();
      };
      if (config.serverCallsOutsideNgZone === true) {
        if (!this.ngZone) {
          console.error("NGXLogger: NgZone is not provided and serverCallsOutsideNgZone is set to true");
          return;
        }
        this.ngZone.runOutsideAngular(logOnServerAction);
      } else {
        logOnServerAction();
      }
    }
    static ɵfac = function NGXLoggerServerService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NGXLoggerServerService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NGXLoggerServerService,
      factory: NGXLoggerServerService.ɵfac
    });
  }
  return NGXLoggerServerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Injection token of logger writer service
 */
const TOKEN_LOGGER_WRITER_SERVICE = /*#__PURE__*/new InjectionToken('TOKEN_LOGGER_WRITER_SERVICE');
const DEFAULT_COLOR_SCHEME = ["purple", "teal", "gray", "gray", "yellow", "red", "red"];
let NGXLoggerWriterService = /*#__PURE__*/(() => {
  class NGXLoggerWriterService {
    /** List of functions called when preparing meta string */
    prepareMetaStringFuncs = [this.getTimestampToWrite.bind(this), this.getLevelToWrite.bind(this), this.getFileDetailsToWrite.bind(this), this.getContextToWrite.bind(this)];
    /** Write the content sent to the log function to the console */
    writeMessage(metadata, config) {
      const metaString = this.prepareMetaString(metadata, config);
      this.logFunc(metadata, config, metaString);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getTimestampToWrite(metadata, config) {
      return metadata.timestamp ?? "";
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getLevelToWrite(metadata, config) {
      return getLevelName(metadata.level) ?? getLevelName(NgxLogLevels.OFF);
    }
    getFileDetailsToWrite(metadata, config) {
      return config.disableFileDetails === true ? "" : `[${metadata.fileName}:${metadata.lineNumber.toString()}:${metadata.columnNumber.toString()}]`;
    }
    getContextToWrite(metadata, config) {
      return config.context ? `{${config.context}}` : "";
    }
    /** Generate a "meta" string that is displayed before the content sent to the log function */
    prepareMetaString(metadata, config) {
      let metaString = "";
      for (const prepareMetaStringFunc of this.prepareMetaStringFuncs) {
        const metaItem = prepareMetaStringFunc(metadata, config);
        if (metaItem) {
          metaString = metaString + " " + metaItem;
        }
      }
      return metaString.trim();
    }
    /** Get the color to use when writing to console */
    getColor(metadata, config) {
      const configColorScheme = config.colorScheme ?? DEFAULT_COLOR_SCHEME;
      // this is needed to avoid a build error
      if (metadata.level === NgxLogLevels.OFF) {
        return DEFAULT_COLOR_SCHEME[NgxLogLevels.INFO];
      }
      return configColorScheme[metadata.level] ?? DEFAULT_COLOR_SCHEME[NgxLogLevels.INFO];
    }
    /** Log to the console */
    logFunc(metadata, config, metaString) {
      const color = this.getColor(metadata, config);
      // make sure additional isn't null or undefined so that ...additional doesn't error
      const additional = metadata.additional ?? [];
      switch (metadata.level) {
        case NgxLogLevels.WARN:
          console.warn(`%c${metaString}`, `color:${color}`, metadata.message, ...additional);
          break;
        case NgxLogLevels.ERROR:
        case NgxLogLevels.FATAL:
          console.error(`%c${metaString}`, `color:${color}`, metadata.message, ...additional);
          break;
        case NgxLogLevels.INFO:
          console.info(`%c${metaString}`, `color:${color}`, metadata.message, ...additional);
          break;
        //  Disabling console.trace since the stack trace is not helpful. it is showing the stack trace of
        // the console.trace statement
        // case NgxLogLevels.TRACE:
        //   console.trace(`%c${metaString}`, `color:${color}`, message, ...additional);
        //   break;
        case NgxLogLevels.DEBUG:
          console.debug(`%c${metaString}`, `color:${color}`, metadata.message, ...additional);
          break;
        default:
          console.log(`%c${metaString}`, `color:${color}`, metadata.message, ...additional);
      }
    }
    static ɵfac = function NGXLoggerWriterService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NGXLoggerWriterService)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NGXLoggerWriterService,
      factory: NGXLoggerWriterService.ɵfac
    });
  }
  return NGXLoggerWriterService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @deprecated use NgxLogLevel instead
 * @see NgxLogLevel
 * @see NgxLogLevels
 */
var NgxLoggerLevel = /*#__PURE__*/function (NgxLoggerLevel) {
  NgxLoggerLevel[NgxLoggerLevel["TRACE"] = 0] = "TRACE";
  NgxLoggerLevel[NgxLoggerLevel["DEBUG"] = 1] = "DEBUG";
  NgxLoggerLevel[NgxLoggerLevel["INFO"] = 2] = "INFO";
  NgxLoggerLevel[NgxLoggerLevel["LOG"] = 3] = "LOG";
  NgxLoggerLevel[NgxLoggerLevel["WARN"] = 4] = "WARN";
  NgxLoggerLevel[NgxLoggerLevel["ERROR"] = 5] = "ERROR";
  NgxLoggerLevel[NgxLoggerLevel["FATAL"] = 6] = "FATAL";
  NgxLoggerLevel[NgxLoggerLevel["OFF"] = 7] = "OFF";
  return NgxLoggerLevel;
}(NgxLoggerLevel || {});
/**
 * Main logger service for NGX Logging Kit
 * Provides logging functionality with configurable output to console and server
 *
 * @example
 * ```typescript
 * export class MyComponent {
 *   private readonly logger = inject(NGXLogger);
 *
 *   logSomething() {
 *     this.logger.debug('Debug message');
 *     this.logger.info('Info message', { data: 'value' });
 *     this.logger.error('Error occurred', error);
 *   }
 * }
 * ```
 */
let NGXLogger = /*#__PURE__*/(() => {
  class NGXLogger {
    metadataService = inject(TOKEN_LOGGER_METADATA_SERVICE);
    ruleService = inject(TOKEN_LOGGER_RULES_SERVICE);
    mapperService = inject(TOKEN_LOGGER_MAPPER_SERVICE);
    writerService = inject(TOKEN_LOGGER_WRITER_SERVICE);
    serverService = inject(TOKEN_LOGGER_SERVER_SERVICE);
    configEngine;
    loggerMonitor;
    constructor() {
      const config = inject(TOKEN_LOGGER_CONFIG);
      const configEngineFactory = inject(TOKEN_LOGGER_CONFIG_ENGINE_FACTORY);
      this.configEngine = configEngineFactory.provideConfigEngine(config);
    }
    /**
     * Get the current log level
     * @returns The configured minimum log level
     */
    get level() {
      return this.configEngine.level;
    }
    /**
     * Get the current server log level
     * @returns The configured minimum server log level
     */
    get serverLogLevel() {
      return this.configEngine.serverLogLevel;
    }
    /**
     * Log a trace message
     * @param message Message to log (can be a function for lazy evaluation)
     * @param additional Additional parameters to log
     */
    trace(message, ...additional) {
      this.log_internal(NgxLogLevels.TRACE, message, additional);
    }
    /**
     * Log a debug message
     * @param message Message to log (can be a function for lazy evaluation)
     * @param additional Parameters to log
     */
    debug(message, ...additional) {
      this.log_internal(NgxLogLevels.DEBUG, message, additional);
    }
    /**
     * Log an info message
     * @param message Message to log (can be a function for lazy evaluation)
     * @param additional Additional parameters to log
     */
    info(message, ...additional) {
      this.log_internal(NgxLogLevels.INFO, message, additional);
    }
    /**
     * Log a general log message
     * @param message Message to log (can be a function for lazy evaluation)
     * @param additional Additional parameters to log
     */
    log(message, ...additional) {
      this.log_internal(NgxLogLevels.LOG, message, additional);
    }
    /**
     * Log a warning message
     * @param message Message to log (can be a function for lazy evaluation)
     * @param additional Additional parameters to log
     */
    warn(message, ...additional) {
      this.log_internal(NgxLogLevels.WARN, message, additional);
    }
    /**
     * Log an error message
     * @param message Message to log (can be a function for lazy evaluation)
     * @param additional Additional parameters to log
     */
    error(message, ...additional) {
      this.log_internal(NgxLogLevels.ERROR, message, additional);
    }
    /**
     * Log a fatal error message
     * @param message Message to log (can be a function for lazy evaluation)
     * @param additional Additional parameters to log
     */
    fatal(message, ...additional) {
      this.log_internal(NgxLogLevels.FATAL, message, additional);
    }
    /**
     * Register a INGXLoggerMonitor that will be triggered when a log is either written or sent to server
     *
     * There is only one monitor, registering one will overwrite the last one if there was one
     * @param monitor Monitor instance to register
     */
    registerMonitor(monitor) {
      this.loggerMonitor = monitor;
    }
    /**
     * Set config of logger
     *
     * Warning: This overwrites all the config, if you want to update only one property,
     * you should use @see getConfigSnapshot first
     * @param config New configuration
     */
    updateConfig(config) {
      this.configEngine.updateConfig(config);
    }
    /**
     * Partially update the logger configuration
     * Only the provided properties will be updated
     * @param partialConfig Partial configuration to merge
     */
    partialUpdateConfig(partialConfig) {
      this.configEngine.partialUpdateConfig(partialConfig);
    }
    /**
     * Get a snapshot of the current logger configuration
     * @returns Current configuration
     */
    getConfigSnapshot() {
      return this.configEngine.getConfig();
    }
    /**
     * Flush the server queue immediately
     * Sends all queued logs to the server
     */
    flushServerQueue() {
      this.serverService.flushQueue(this.getConfigSnapshot());
    }
    /**
     * Log with a specific configuration
     * Used by ContextLogger to apply feature-specific configurations
     * @param level Log level
     * @param config Configuration to use
     * @param message Message to log
     * @param additional Additional parameters
     */
    logWithConfig(level, config, message, ...additional) {
      this.log_internal(level, message, additional, config);
    }
    /**
     * Internal logging implementation
     * @param level Log level
     * @param message Message to log
     * @param additional Additional parameters
     */
    log_internal(level, message, additional = [], config = this.configEngine.getConfig()) {
      const shouldCallWriter = this.ruleService.shouldCallWriter(level, config, message, additional);
      const shouldCallServer = this.ruleService.shouldCallServer(level, config, message, additional);
      const shouldCallMonitor = this.ruleService.shouldCallMonitor(level, config, message, additional);
      if (!shouldCallWriter && !shouldCallServer && !shouldCallMonitor) {
        // If nothing is to be called we return
        return;
      }
      const metadata = this.metadataService.getMetadata(level, config, message, additional);
      this.mapperService.getLogPosition(config, metadata).pipe(take(1)).subscribe(logPosition => {
        metadata.fileName = logPosition.fileName;
        metadata.lineNumber = logPosition.lineNumber;
        metadata.columnNumber = logPosition.columnNumber;
        if (shouldCallMonitor && this.loggerMonitor) {
          this.loggerMonitor.onLog(metadata, config);
        }
        if (shouldCallWriter) {
          this.writerService.writeMessage(metadata, config);
        }
        if (shouldCallServer) {
          this.serverService.sendToServer(metadata, config);
        }
      });
    }
    static ɵfac = function NGXLogger_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NGXLogger)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NGXLogger,
      factory: NGXLogger.ɵfac,
      providedIn: "root"
    });
  }
  return NGXLogger;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @deprecated Use `provideLogger` instead.
 */
let LoggerModule = /*#__PURE__*/(() => {
  class LoggerModule {
    static forRoot(config, customProvider) {
      customProvider ??= {};
      // default config provider
      if (!customProvider.configProvider) {
        customProvider.configProvider = {
          provide: TOKEN_LOGGER_CONFIG,
          useValue: config || {}
        };
      } else {
        // if the user provided its own config, we just make sure the injection token is correct
        if (customProvider.configProvider.provide !== TOKEN_LOGGER_CONFIG) {
          throw new Error(`Wrong injection token for configProvider, it should be ${TOKEN_LOGGER_CONFIG.toString()} and you used ${customProvider.configProvider.provide}`);
        }
      }
      // default configEngine provider
      if (!customProvider.configEngineFactoryProvider) {
        customProvider.configEngineFactoryProvider = {
          provide: TOKEN_LOGGER_CONFIG_ENGINE_FACTORY,
          useClass: NGXLoggerConfigEngineFactory
        };
      } else {
        // if the user provided its own configEngineFactory, we just make sure the injection token is correct
        if (customProvider.configEngineFactoryProvider.provide !== TOKEN_LOGGER_CONFIG_ENGINE_FACTORY) {
          throw new Error(`Wrong injection token for configEngineFactoryProvider, it should be '${TOKEN_LOGGER_CONFIG_ENGINE_FACTORY.toString()}' and you used '${customProvider.configEngineFactoryProvider.provide}'`);
        }
      }
      // default metadata provider
      if (!customProvider.metadataProvider) {
        customProvider.metadataProvider = {
          provide: TOKEN_LOGGER_METADATA_SERVICE,
          useClass: NGXLoggerMetadataService
        };
      } else {
        // if the user provided its own metadataService, we just make sure the injection token is correct
        if (customProvider.metadataProvider.provide !== TOKEN_LOGGER_METADATA_SERVICE) {
          throw new Error(`Wrong injection token for metadataProvider, it should be '${TOKEN_LOGGER_METADATA_SERVICE.toString()}' and you used '${customProvider.metadataProvider.provide}'`);
        }
      }
      // default rule provider
      if (!customProvider.ruleProvider) {
        customProvider.ruleProvider = {
          provide: TOKEN_LOGGER_RULES_SERVICE,
          useClass: NGXLoggerRulesService
        };
      } else {
        // if the user provided its own ruleService, we just make sure the injection token is correct
        if (customProvider.ruleProvider.provide !== TOKEN_LOGGER_RULES_SERVICE) {
          throw new Error(`Wrong injection token for ruleProvider, it should be '${TOKEN_LOGGER_RULES_SERVICE.toString()}' and you used '${customProvider.ruleProvider.provide}'`);
        }
      }
      // default mapper provider
      if (!customProvider.mapperProvider) {
        customProvider.mapperProvider = {
          provide: TOKEN_LOGGER_MAPPER_SERVICE,
          useClass: NGXLoggerMapperService
        };
      } else {
        // if the user provided its own mapperService, we just make sure the injection token is correct
        if (customProvider.mapperProvider.provide !== TOKEN_LOGGER_MAPPER_SERVICE) {
          throw new Error(`Wrong injection token for mapperProvider, it should be '${TOKEN_LOGGER_MAPPER_SERVICE.toString()}' and you used '${customProvider.mapperProvider.provide}'`);
        }
      }
      // default writer provider
      if (!customProvider.writerProvider) {
        customProvider.writerProvider = {
          provide: TOKEN_LOGGER_WRITER_SERVICE,
          useClass: NGXLoggerWriterService
        };
      } else {
        // if the user provided its own writerService, we just make sure the injection token is correct
        if (customProvider.writerProvider.provide !== TOKEN_LOGGER_WRITER_SERVICE) {
          throw new Error(`Wrong injection token for writerProvider, it should be '${TOKEN_LOGGER_WRITER_SERVICE.toString()}' and you used '${customProvider.writerProvider.provide}'`);
        }
      }
      // default server provider
      if (!customProvider.serverProvider) {
        customProvider.serverProvider = {
          provide: TOKEN_LOGGER_SERVER_SERVICE,
          useClass: NGXLoggerServerService
        };
      } else {
        // if the user provided its own serverService, we just make sure the injection token is correct
        if (customProvider.serverProvider.provide !== TOKEN_LOGGER_SERVER_SERVICE) {
          throw new Error(`Wrong injection token for serverProvider, it should be '${TOKEN_LOGGER_SERVER_SERVICE.toString()}' and you used '${customProvider.writerProvider.provide}'`);
        }
      }
      return {
        ngModule: LoggerModule,
        providers: [NGXLogger, customProvider.configProvider, customProvider.configEngineFactoryProvider, customProvider.metadataProvider, customProvider.ruleProvider, customProvider.mapperProvider, customProvider.writerProvider, customProvider.serverProvider]
      };
    }
    static forChild() {
      // todo : this forChild is useless for now because nothing is different from forRoot.
      // This should be implemented so that user can change the providers in the forChild
      return {
        ngModule: LoggerModule
      };
    }
    static ɵfac = function LoggerModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || LoggerModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: LoggerModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return LoggerModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function provideLogger(config, options = {}) {
  return makeEnvironmentProviders([{
    provide: TOKEN_LOGGER_CONFIG,
    useValue: config
  }, options.configEngineFactoryProvider ?? {
    provide: TOKEN_LOGGER_CONFIG_ENGINE_FACTORY,
    useClass: NGXLoggerConfigEngineFactory
  }, options.metadataProvider ?? {
    provide: TOKEN_LOGGER_METADATA_SERVICE,
    useClass: NGXLoggerMetadataService
  }, options.ruleProvider ?? {
    provide: TOKEN_LOGGER_RULES_SERVICE,
    useClass: NGXLoggerRulesService
  }, options.mapperProvider ?? {
    provide: TOKEN_LOGGER_MAPPER_SERVICE,
    useClass: NGXLoggerMapperService
  }, options.writerProvider ?? {
    provide: TOKEN_LOGGER_WRITER_SERVICE,
    useClass: NGXLoggerWriterService
  }, options.serverProvider ?? {
    provide: TOKEN_LOGGER_SERVER_SERVICE,
    useClass: NGXLoggerServerService
  }, NGXLogger]);
}
class ContextLogger extends NGXLogger {
  parent;
  context;
  constructor(parent, context) {
    super();
    this.parent = parent;
    this.context = context;
  }
  get level() {
    return this.parent.level;
  }
  get serverLogLevel() {
    return this.parent.serverLogLevel;
  }
  trace(message, ...additional) {
    const {
      msg,
      args
    } = this.decorate(message, additional);
    this.parent.logWithConfig(NgxLogLevels.TRACE, this.getFeatureConfig(), msg, ...args);
  }
  debug(message, ...additional) {
    const {
      msg,
      args
    } = this.decorate(message, additional);
    this.parent.logWithConfig(NgxLogLevels.DEBUG, this.getFeatureConfig(), msg, ...args);
  }
  info(message, ...additional) {
    const {
      msg,
      args
    } = this.decorate(message, additional);
    this.parent.logWithConfig(NgxLogLevels.INFO, this.getFeatureConfig(), msg, ...args);
  }
  log(message, ...additional) {
    const {
      msg,
      args
    } = this.decorate(message, additional);
    this.parent.logWithConfig(NgxLogLevels.LOG, this.getFeatureConfig(), msg, ...args);
  }
  warn(message, ...additional) {
    const {
      msg,
      args
    } = this.decorate(message, additional);
    this.parent.logWithConfig(NgxLogLevels.WARN, this.getFeatureConfig(), msg, ...args);
  }
  error(message, ...additional) {
    const {
      msg,
      args
    } = this.decorate(message, additional);
    this.parent.logWithConfig(NgxLogLevels.ERROR, this.getFeatureConfig(), msg, ...args);
  }
  fatal(message, ...additional) {
    const {
      msg,
      args
    } = this.decorate(message, additional);
    this.parent.logWithConfig(NgxLogLevels.FATAL, this.getFeatureConfig(), msg, ...args);
  }
  registerMonitor(monitor) {
    this.parent.registerMonitor(monitor);
  }
  updateConfig(config) {
    this.parent.updateConfig(config);
  }
  partialUpdateConfig(partialConfig) {
    this.parent.partialUpdateConfig(partialConfig);
  }
  getConfigSnapshot() {
    return this.parent.getConfigSnapshot();
  }
  flushServerQueue() {
    this.parent.flushServerQueue();
  }
  getFeatureConfig() {
    const config = this.parent.getConfigSnapshot();
    const featureConfig = config.features?.[this.context];
    if (featureConfig) {
      return {
        ...config,
        level: featureConfig.logLevel,
        serverLogLevel: featureConfig.serverLogLevel ?? config.serverLogLevel
      };
    }
    return config;
  }
  decorate(message, additional = []) {
    const prefix = `[${this.context}]`;
    if (typeof message === "string") {
      return {
        msg: `${prefix} ${message}`,
        args: additional
      };
    }
    if (typeof message === "function") {
      const wrappedFn = () => {
        const result = message();
        if (typeof result === "string") {
          return `${prefix} ${result}`;
        }
        return [prefix, result];
      };
      return {
        msg: wrappedFn,
        args: additional
      };
    }
    if (message === undefined) {
      return {
        msg: prefix,
        args: additional
      };
    }
    return {
      msg: prefix,
      args: [message, ...additional]
    };
  }
}
/**
 * Injects a logger with a specific context.
 * The logger will prepend the context to all log messages.
 *
 * @param context The context string to prepend (e.g. 'MyComponent')
 * @returns An NGXLogger instance that delegates to the root logger
 *
 * @example
 * ```typescript
 * export class MyComponent {
 *   private readonly logger = injectLogger('MyComponent');
 *
 *   ngOnInit() {
 *     this.logger.info('Initialized'); // Logs: [MyComponent] Initialized
 *   }
 * }
 * ```
 */
function injectLogger(context) {
  const parent = inject(NGXLogger);
  return new ContextLogger(parent, context);
}

/*
 * Public API Surface of ngx-logging-kit
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_COLOR_SCHEME, LoggerModule, NGXLogger, NGXLoggerConfigEngine, NGXLoggerConfigEngineFactory, NGXLoggerMapperService, NGXLoggerMetadataService, NGXLoggerMonitor, NGXLoggerRulesService, NGXLoggerServerService, NGXLoggerWriterService, NgxLoggerLevel, TOKEN_LOGGER_CONFIG, TOKEN_LOGGER_CONFIG_ENGINE_FACTORY, TOKEN_LOGGER_MAPPER_SERVICE, TOKEN_LOGGER_METADATA_SERVICE, TOKEN_LOGGER_RULES_SERVICE, TOKEN_LOGGER_SERVER_SERVICE, TOKEN_LOGGER_WRITER_SERVICE, injectLogger, provideLogger };
